import React, { useState } from "react";

import {
  FormContext,
  useForm,
} from "react-hook-form/dist/react-hook-form.ie11";
import { addDays, isAfter, isPast } from "date-fns";

import { getCalenderEventsQueryInput } from "@hubcapp/components/Calendar/helpers";
import { selectCalendarInitialDate, selectCalendarView } from "@hubcapp/state";
import { useTealium } from "@hubcapp/context";

import { CancelLessonFormFields, StateProviderProps } from "./types";
import { useSelector } from "react-redux";
import CancelLessonModal from "./CancelLessonModal";
import useCancelLesson from "./hooks/useCancelLesson";
import validationSchema from "./validationSchema";

import { fireOnce } from "@ds-developer/frontend";

export const defaultValues = {
  reason: null,
  refundLearner: true,
};

const CancelLessonModalStateProvider: React.FC<StateProviderProps> = ({
  lessonId,
  lessonStartDateTime,
  open,
  initialValues = {},
  lessonPrePaid = true,
  onConfirmClicked = () => {},
  onCompleted = () => {},
  onCancelClicked = () => {},
}) => {
  const initialDate =
    useSelector(selectCalendarInitialDate) || new Date(Date.now());
  const view = useSelector(selectCalendarView);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { trackEvent } = useTealium();

  const mutationComplete = () => {
    setSubmitting(false);
    onCompleted();
  };

  const [cancelLesson, { loading }] = useCancelLesson({
    onCompleted: mutationComplete,
  });

  const methods = useForm<CancelLessonFormFields>({
    defaultValues: {
      ...defaultValues,
      ...{
        refundLearner: lessonPrePaid && defaultValues.refundLearner,
      },
      ...initialValues,
    },
    mode: "onChange",
    validationSchema,
  });

  const submit = (values: CancelLessonFormFields) => {
    setSubmitting(true);
    onConfirmClicked();
    cancelLesson({
      variables: {
        id: lessonId,
        reason: values.reason === "" ? null : values.reason,
        refundLearner: values.refundLearner,
        ...getCalenderEventsQueryInput(initialDate, view),
      },
    });
    trackEvent({
      eventCategory: "Calendar - View Lesson",
      eventAction: "Button Click",
      eventLabel: "Confirm Delete",
    });
  };

  const handleOnClickTextField = () => {
    fireOnce(
      "Calendar - View Lesson|Interaction|Reason For Cancellation",
      () => {
        trackEvent({
          eventCategory: "Calendar - View Lesson",
          eventAction: "Interaction",
          eventLabel: "Reason For Cancellation",
        });
      },
    );
  };

  const handleOnClickRefundRadio = (selected: boolean) => {
    trackEvent({
      eventCategory: "Calendar - View Lesson",
      eventAction: "Interaction",
      eventLabel: `${"Refund This Lesson"} - ${
        selected == true ? "Yes" : "No"
      }`,
    });
  };

  const shouldDisableRefundToggle =
    !lessonPrePaid ||
    isPast(new Date(lessonStartDateTime)) ||
    isAfter(new Date(lessonStartDateTime), addDays(new Date(), 2));

  return (
    <FormContext {...methods}>
      <CancelLessonModal
        handleCancel={onCancelClicked}
        loading={loading}
        open={open}
        disableRefundToggle={shouldDisableRefundToggle}
        submitting={submitting}
        handleSubmit={methods.handleSubmit(submit)}
        handleOnClickTextField={handleOnClickTextField}
        handleOnClickRefundRadio={handleOnClickRefundRadio}
      />
    </FormContext>
  );
};

export default CancelLessonModalStateProvider;
